/**
 * Self-contained demo data for the Opportunity Intelligence module.
 *
 * Content mirrors the designer mock (Northwind Beverages · Opportunities).
 * The four views (Realization, By pressure, By source, Lifecycle) are all
 * projections of this single dataset. Typed so it can later be swapped for
 * a real API.
 */

export type OppStage = 'detected' | 'accepted' | 'in_execution' | 'realized' | 'abandoned';

/** Drives the source chip icon + colour. */
export type OppSourceKind = 'pressure' | 'agent' | 'should_cost' | 'benchmarking' | 'risk' | 'finder' | 'manual';

export type OpportunitySource = {
    label: string;
    kind: OppSourceKind;
};

export type Opportunity = {
    id: string;            // internal unique id
    sitId?: string;        // optional initiative-tracker id, shown as a badge (e.g. SIT-1018)
    title: string;
    detector: string;      // short detector label, e.g. "Licence rationalisation"
    summary: string;       // the descriptive tail after the detector
    source: OpportunitySource;
    stage: OppStage;
    confidence: number;    // 0..100
    owner: string;
    identifiedValue: number;
    currency: string;
    ebitEffective?: boolean;
    realized?: boolean;
};

/** A pressured budget line (By pressure view). */
export type BudgetLine = {
    id: string;
    glCode: string;        // e.g. "GL 7100"
    costCenter: string;    // e.g. "IT-GLB-01"
    name: string;
    owner: string;
    scope: string;         // e.g. "Group Functions"
    savingsNeeded: number;
    progress: number;      // 0..1 of the challenge target already secured
    currency: string;
};

export type OpportunityKpis = {
    identifiedValue: number;
    realizedValue: number;
    realizationRate: number; // 0..1
    inExecutionValue: number;
    currency: string;
    liveCount: number;
    realizedCount: number;
    inExecutionCount: number;
    abandonedHidden: number;
};

const CURRENCY = 'EUR';

export const OPPORTUNITY_KPIS: OpportunityKpis = {
    identifiedValue: 2_180_000,
    realizedValue: 370_000,
    realizationRate: 0.17,
    inExecutionValue: 652_000,
    currency: CURRENCY,
    liveCount: 13,
    realizedCount: 2,
    inExecutionCount: 3,
    abandonedHidden: 1,
};

export const OPPORTUNITY_SUBTITLE =
    'One pipeline, many detectors · 13 live · 2 realized · fed by budget pressure, ' +
    'the Demand Agent, Should-Cost, benchmarking, risk and 9 saved finders.';

export const PRESSURE_HEADLINE = '3 budget lines need attention this quarter';
export const PRESSURE_INTRO =
    "You need to deliver €2.38M in combined savings across the top three pressured lines to hit " +
    "Northwind's FY26 challenge targets. Pick a line to see scoped opportunities — Atlas has already " +
    'grouped them by impact.';

const OPPORTUNITIES: Opportunity[] = [
    {
        id: 'opp-salesforce-licence',
        sitId: 'SIT-1018',
        title: 'Licence rationalisation — Salesforce',
        detector: 'Licence rationalisation',
        summary: '12% of Salesforce seats unused 90+ days',
        source: {label: 'IT — SaaS', kind: 'pressure'},
        stage: 'realized',
        confidence: 95,
        owner: 'Wim Tellegen',
        identifiedValue: 198_000,
        currency: CURRENCY,
        ebitEffective: true,
        realized: true,
    },
    {
        id: 'opp-halcyon-fm',
        sitId: 'SIT-1097',
        title: 'Halcyon FM consolidation',
        detector: 'Supplier consolidation',
        summary: '9 suppliers, HHI 0.08 — consolidate to top 3 preferred',
        source: {label: 'ATL Media Cost Reduction', kind: 'finder'},
        stage: 'realized',
        confidence: 84,
        owner: 'Pieter Hartog',
        identifiedValue: 172_000,
        currency: CURRENCY,
        ebitEffective: true,
        realized: true,
    },
    {
        id: 'opp-contract-leakage',
        sitId: 'SIT-1102',
        title: 'Contract leakage on master agreement',
        detector: 'Contract leakage',
        summary: 'Bring Brightline & Northbound off-contract spend under the master agreement',
        source: {label: 'ATL Media', kind: 'pressure'},
        stage: 'in_execution',
        confidence: 83,
        owner: 'Janneke de Vries',
        identifiedValue: 280_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-brightline-scope',
        sitId: 'SIT-1042',
        title: 'Brightline retainer scope drift',
        detector: 'Scope drift',
        summary: 'Reconcile scope before Q3 renewal. Sub-suppliers under the master agreement.',
        source: {label: 'Demand Management Agent', kind: 'agent'},
        stage: 'in_execution',
        confidence: 67,
        owner: 'Janneke de Vries',
        identifiedValue: 248_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-short-notice-travel',
        sitId: 'SIT-1027',
        title: 'Short-notice travel bookings',
        detector: 'Frequency anomaly',
        summary: 'Enforce 5-day advance booking. Add virtual-first prompt.',
        source: {label: 'Demand Management Agent', kind: 'agent'},
        stage: 'in_execution',
        confidence: 83,
        owner: 'Renée Lammers',
        identifiedValue: 124_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-q4-creative-redundancy',
        title: 'Q4 creative redundancy — EMEA',
        detector: 'Demand redundancy',
        summary: 'Repurpose assets from library. Pre-commission library check.',
        source: {label: 'Demand Management Agent', kind: 'agent'},
        stage: 'accepted',
        confidence: 61,
        owner: 'Maartje Bakker',
        identifiedValue: 180_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-tail-spend',
        title: 'Tail spend rationalisation',
        detector: 'Tail spend rationalisation',
        summary: 'Consolidate suppliers under €50k into framework agreement',
        source: {label: 'ATL Media', kind: 'pressure'},
        stage: 'accepted',
        confidence: 82,
        owner: 'Janneke de Vries',
        identifiedValue: 145_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-citrus-single-source',
        title: 'Citrus Atlantic single-source',
        detector: 'Risk mitigation',
        summary: '62% single-source share — qualify a second source',
        source: {label: 'Supplier 360 Risk', kind: 'risk'},
        stage: 'accepted',
        confidence: 96,
        owner: 'Janneke de Vries',
        identifiedValue: 90_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-atlassian-renewal',
        title: 'Atlassian Cloud renewal leverage',
        detector: 'Renewal negotiation',
        summary: 'Renewal in 56 days, 38% inactive seats',
        source: {label: 'SaaS Renewals Q3', kind: 'finder'},
        stage: 'accepted',
        confidence: 92,
        owner: 'Wim Tellegen',
        identifiedValue: 64_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-mro-catalog',
        title: 'MRO catalog enablement',
        detector: 'Tail spend rationalisation',
        summary: '3,840 lines · avg €310 · catalog enablement candidate',
        source: {label: 'MRO Tail Spend Sweep', kind: 'finder'},
        stage: 'detected',
        confidence: 80,
        owner: 'Esther Volk',
        identifiedValue: 212_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-corrugated-should-cost',
        title: 'Corrugated should-cost gap',
        detector: 'Should-cost gap',
        summary: 'Verallia overhead component 53% above modelled — re-quote candidate',
        source: {label: 'Should-Cost Engine', kind: 'should_cost'},
        stage: 'detected',
        confidence: 91,
        owner: 'Esther Volk',
        identifiedValue: 148_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-aws-payment-terms',
        title: 'AWS payment-term optimisation',
        detector: 'Payment terms',
        summary: 'Avg DPO 32 vs industry 45; 1.6× spend leverage',
        source: {label: 'SaaS Renewals Q3', kind: 'finder'},
        stage: 'detected',
        confidence: 76,
        owner: 'Wim Tellegen',
        identifiedValue: 96_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-datadog-peer-median',
        title: 'Above peer median — Datadog',
        detector: 'Benchmark deviation',
        summary: 'Unit price at P82 of the cohort — renegotiate to median',
        source: {label: 'Cross-Customer Benchmark', kind: 'benchmarking'},
        stage: 'detected',
        confidence: 81,
        owner: 'Wim Tellegen',
        identifiedValue: 88_000,
        currency: CURRENCY,
    },
    {
        id: 'opp-plume-off-contract',
        title: 'Off-contract — Plume Search',
        detector: 'Off-contract leakage',
        summary: 'Already under master agreement — false positive',
        source: {label: 'Manual', kind: 'manual'},
        stage: 'abandoned',
        confidence: 32,
        owner: 'Maartje Bakker',
        identifiedValue: 42_000,
        currency: CURRENCY,
    },
];

const BUDGET_LINES: BudgetLine[] = [
    {
        id: 'gl-7100',
        glCode: 'GL 7100',
        costCenter: 'IT-GLB-01',
        name: 'Indirect IT — SaaS & Licences',
        owner: 'Wim Tellegen',
        scope: 'Group Functions',
        savingsNeeded: 1_030_000,
        progress: 0.45,
        currency: CURRENCY,
    },
    {
        id: 'gl-6120',
        glCode: 'GL 6120',
        costCenter: 'MKT-NL-03',
        name: 'Digital Media Buying',
        owner: 'Maartje Bakker',
        scope: 'Northwind NL',
        savingsNeeded: 720_000,
        progress: 0.62,
        currency: CURRENCY,
    },
    {
        id: 'gl-6100',
        glCode: 'GL 6100',
        costCenter: 'MKT-NL-01',
        name: 'ATL Media & Production',
        owner: 'Janneke de Vries',
        scope: 'Northwind NL',
        savingsNeeded: 630_000,
        progress: 0.78,
        currency: CURRENCY,
    },
];

export function getOpportunities(): Opportunity[] {
    return OPPORTUNITIES;
}

/** Live = everything except abandoned (matches the "13 live" headline). */
export function getLiveOpportunities(): Opportunity[] {
    return OPPORTUNITIES.filter(o => o.stage !== 'abandoned');
}

export function getBudgetLines(): BudgetLine[] {
    return BUDGET_LINES;
}
